import React, { useState } from "react";
import { Navigate, Outlet, useNavigate, NavLink } from "react-router-dom";
import { LogOut, FileText, Inbox, Home, Sun, KeyRound, User } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { clearToken, getToken } from "./api";
import ChangePasswordDialog from "./ChangePasswordDialog";

export default function AdminLayout() {
  const navigate = useNavigate();
  const token = getToken();
  const [pwdOpen, setPwdOpen] = useState(false);

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-extrabold text-slate-900 leading-none">Zenova Admin</div>
              <div className="text-[10px] text-slate-500 tracking-widest">CONTENT &amp; LEADS</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `px-3 h-9 inline-flex items-center gap-1.5 rounded-md text-sm font-medium ${
                  isActive ? "bg-[#0a4f47] text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Inbox className="w-4 h-4" /> Enquiries
            </NavLink>
            <NavLink
              to="/admin/content"
              className={({ isActive }) =>
                `px-3 h-9 inline-flex items-center gap-1.5 rounded-md text-sm font-medium ${
                  isActive ? "bg-[#0a4f47] text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <FileText className="w-4 h-4" /> Content
            </NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0a4f47] px-3 h-9 rounded-md hover:bg-slate-100"
            >
              <Home className="w-4 h-4" /> View site
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-9 gap-1.5 border-slate-300">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Account</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Signed in as admin</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwdOpen(true)} className="gap-2 cursor-pointer">
                  <KeyRound className="w-4 h-4" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="gap-2 cursor-pointer text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-slate-200 px-4 py-2 flex gap-2">
          <NavLink to="/admin" end className={({ isActive }) => `flex-1 text-center py-2 rounded-md text-sm font-medium ${isActive ? "bg-[#0a4f47] text-white" : "text-slate-700 bg-slate-100"}`}>Enquiries</NavLink>
          <NavLink to="/admin/content" className={({ isActive }) => `flex-1 text-center py-2 rounded-md text-sm font-medium ${isActive ? "bg-[#0a4f47] text-white" : "text-slate-700 bg-slate-100"}`}>Content</NavLink>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
        <Outlet />
      </main>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
